/**
 * PPT 应用 · API 客户端（自平台 services/api.ts 拆出，随应用独立演进）
 */
import { platformFetch, streamJsonEvents } from '../../platform/http';

export interface PptImageRef {
  id: string;
  status: string;
  resultUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface PptSlide {
  id: string;
  sortOrder: number;
  slideType: string;
  title: string | null;
  contentPrompt: string | null;
  imageJobId: string | null;
  generationStatus: string;
  referenceMode: string;
  image: PptImageRef | null;
  variants: PptImageRef[];
}

export interface PptProject {
  id: string;
  title: string;
  topic: string | null;
  purpose: string | null;
  pageCount: number;
  baseModelId: string | null;
  inpaintModelId: string | null;
  generationQuality: 'low' | 'medium' | 'high';
  aspectRatio: string;
  stylePreset: string;
  status: string;
  backgroundPrompt: string | null;
  backgroundCandidateIds: string[];
  backgroundImageJobId: string | null;
  backgroundImage: PptImageRef | null;
  backgroundCandidates: PptImageRef[];
  slides: PptSlide[];
  createdAt: string;
  updatedAt: string;
}

export interface PptAiOutlineSlide {
  sortOrder: number;
  slideType: string;
  title: string;
  contentPrompt: string;
}

export const pptAPI = {
  async listProjects(): Promise<{ data: PptProject[] }> {
    const response = await platformFetch('/ppt/projects');
    if (!response.ok) throw new Error('获取 PPT 项目失败');
    return response.json();
  },

  async getProject(projectId: string): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/projects/${projectId}`);
    if (!response.ok) throw new Error('获取 PPT 项目详情失败');
    return response.json();
  },

  async streamAiOutline(
    projectId: string,
    sourceText: string,
    handlers: {
      onChunk?: (chunk: string) => void;
      onResult?: (result: { slides: PptAiOutlineSlide[] }) => void;
      onError?: (error: string) => void;
      onComplete?: () => void;
    }
  ): Promise<void> {
    const response = await platformFetch(`/ppt/projects/${projectId}/ai-outline/stream`, {
      method: 'POST',
      body: JSON.stringify({ sourceText }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'AI 生成大纲失败' }));
      throw new Error(error.error || 'AI 生成大纲失败');
    }

    if (!response.body) {
      throw new Error('无法读取 AI 生成响应');
    }

    await streamJsonEvents(response, {
      onEvent: (parsed) => {
        if (parsed.error) {
          handlers.onError?.(parsed.error as string);
        } else if ((parsed.result as { slides?: unknown } | undefined)?.slides) {
          handlers.onResult?.(parsed.result as { slides: PptAiOutlineSlide[] });
        } else if (parsed.content) {
          handlers.onChunk?.(parsed.content as string);
        }
      },
      onDone: () => handlers.onComplete?.(),
    });
  },

  async updateProject(projectId: string, payload: {
    title: string;
    baseModelId?: string;
    inpaintModelId?: string;
    generationQuality?: 'low' | 'medium' | 'high';
  }): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '更新 PPT 项目失败' }));
      throw new Error(error.error || '更新 PPT 项目失败');
    }
    return response.json();
  },

  async deleteProject(projectId: string): Promise<{ success: boolean }> {
    const response = await platformFetch(`/ppt/projects/${projectId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '删除 PPT 项目失败' }));
      throw new Error(error.error || '删除 PPT 项目失败');
    }
    return response.json();
  },

  async createProject(payload: {
    title: string;
    topic?: string;
    purpose?: string;
    pageCount?: number;
    aspectRatio?: string;
    stylePreset?: string;
    baseModelId: string;
    inpaintModelId: string;
    generationQuality: 'low' | 'medium' | 'high';
  }): Promise<{ data: PptProject }> {
    const response = await platformFetch('/ppt/projects', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '创建 PPT 项目失败' }));
      throw new Error(error.error || '创建 PPT 项目失败');
    }
    return response.json();
  },

  async generateBackgroundCandidates(
    projectId: string,
    payload?: { prompt?: string; modelId?: string; quality?: string; candidateCount?: number }
  ): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/projects/${projectId}/background/generate`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '生成背景候选失败' }));
      throw new Error(error.error || '生成背景候选失败');
    }
    return response.json();
  },

  async selectBackground(projectId: string, jobId: string): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/projects/${projectId}/background/select`, {
      method: 'POST',
      body: JSON.stringify({ jobId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '选择背景失败' }));
      throw new Error(error.error || '选择背景失败');
    }
    return response.json();
  },

  async generateSlides(
    projectId: string,
    payload: {
      modelId?: string;
      quality?: string;
      slides: Array<{
        slideId?: string;
        sortOrder?: number;
        slideType?: string;
        title?: string;
        contentPrompt?: string;
      }>;
    }
  ): Promise<{ data: PptProject; meta?: { queuedSlideIds: string[] } }> {
    const response = await platformFetch(`/ppt/projects/${projectId}/slides/generate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '批量生成页面失败' }));
      throw new Error(error.error || '批量生成页面失败');
    }
    return response.json();
  },

  async regenerateSlide(
    slideId: string,
    payload: {
      modelId?: string;
      quality?: string;
      slideType?: string;
      title?: string;
      contentPrompt?: string;
    }
  ): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/slides/${slideId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '单页重生成失败' }));
      throw new Error(error.error || '单页重生成失败');
    }
    return response.json();
  },

  async inpaintSlide(
    slideId: string,
    payload: {
      maskImage: string;
      editPrompt?: string;
      modelId?: string;
      quality?: string;
      slideType?: string;
      title?: string;
      contentPrompt?: string;
    }
  ): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/slides/${slideId}/inpaint`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '局部重绘失败' }));
      throw new Error(error.error || '局部重绘失败');
    }
    return response.json();
  },

  async exportProject(projectId: string, format: 'pptx' | 'pdf'): Promise<Blob> {
    const response = await platformFetch(`/ppt/projects/${projectId}/export?format=${format}`, {
      method: 'GET',
      headers: {},
    });
    if (!response.ok) {
      let message = '导出失败';
      try {
        const error = await response.json();
        message = error.error || message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    return response.blob();
  },

  async exportProjectBundle(projectId: string, format: 'pptx' | 'pdf' | 'zip'): Promise<Blob> {
    const response = await platformFetch(`/ppt/projects/${projectId}/export?format=${format}`, {
      method: 'GET',
      headers: {},
    });
    if (!response.ok) {
      let message = '导出失败';
      try {
        const error = await response.json();
        message = error.error || message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    return response.blob();
  },

  async reorderSlides(projectId: string, slideIds: string[]): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/projects/${projectId}/slides/reorder`, {
      method: 'POST',
      body: JSON.stringify({ slideIds }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '页面排序失败' }));
      throw new Error(error.error || '页面排序失败');
    }
    return response.json();
  },

  async createSlide(projectId: string, payload?: { afterSlideId?: string }): Promise<{ data: PptProject; meta?: { slideId: string } }> {
    const response = await platformFetch(`/ppt/projects/${projectId}/slides`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '新增幻灯片失败' }));
      throw new Error(error.error || '新增幻灯片失败');
    }
    return response.json();
  },

  async deleteSlide(slideId: string): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/slides/${slideId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '删除幻灯片失败' }));
      throw new Error(error.error || '删除幻灯片失败');
    }
    return response.json();
  },

  async restoreSlideVariant(slideId: string, jobId: string): Promise<{ data: PptProject }> {
    const response = await platformFetch(`/ppt/slides/${slideId}/restore-variant`, {
      method: 'POST',
      body: JSON.stringify({ jobId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '恢复历史版本失败' }));
      throw new Error(error.error || '恢复历史版本失败');
    }
    return response.json();
  },
};
